import { Injectable } from "@angular/core";

export type CalendarEventLinkInput = {
  title: string;
  details?: string;
  location?: string;
  /** If omitted, calendar compose opens without preset start/end (where supported). */
  start?: Date;
  end?: Date;
};

@Injectable({ providedIn: "root" })
export class CalendarLinkService {
  /** Local wall date as YYYY-MM-DD (day-view links use the user’s locale, not UTC). */
  formatLocalDateIso(date: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}`;
  }

  /** Local wall date as YYYYMMDD for Google Calendar day route. */
  private formatLocalDateCompact(date: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, "0");
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
      date.getDate()
    )}`;
  }

  /**
   * Opens Google Calendar day view for the given **local** calendar day (today, past, or future).
   * Uses /r/day/YYYY/M/D because this route consistently lands on the selected day.
   */
  openGoogleCalendarDayView(date: Date): void {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const url = `https://calendar.google.com/calendar/u/0/r/day/${y}/${m}/${d}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  /**
   * Opens Outlook on the web on the given **local** calendar day (work/school; personal accounts often redirect).
   */
  openOutlookWebCalendarDayView(date: Date): void {
    const iso = this.formatLocalDateIso(date);
    const url = new URL("https://outlook.office.com/calendar/view/day");
    url.searchParams.set("date", iso);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  }

  /**
   * Download a small .ics so Apple Calendar and other apps can show the chosen day or exact slot
   * while the user checks for conflicts (no Flowchest server access).
   */
  downloadAvailabilityCheckIcs(input: {
    date: Date;
    start?: Date;
    end?: Date;
    title?: string;
    details?: string;
    fileNamePrefix?: string;
  }): void {
    const title = input.title || "Verify Flowchest time slot";
    const details =
      input.details ||
      "Use this in Apple Calendar or another app to compare with your schedule before you complete the booking.";
    const ics =
      input.start && input.end
        ? this.buildIcsEventContent({
            title,
            details,
            location: "",
            start: input.start,
            end: input.end,
          })
        : this.buildIcsAllDayAvailabilityCheck(input.date, title, details);

    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const iso = this.formatLocalDateIso(input.date);
    const prefix = (input.fileNamePrefix || "flowchest-slot-check")
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase();
    const fileName = `${prefix || "flowchest-slot-check"}-${iso}.ics`;

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private buildIcsAllDayAvailabilityCheck(
    date: Date,
    summary: string,
    description: string
  ): string {
    const pad = (n: number) => `${n}`.padStart(2, "0");
    const y = date.getFullYear();
    const mo = pad(date.getMonth() + 1);
    const da = pad(date.getDate());
    const dayStart = `${y}${mo}${da}`;
    const next = new Date(y, date.getMonth(), date.getDate() + 1);
    const dayEnd = `${next.getFullYear()}${pad(next.getMonth() + 1)}${pad(
      next.getDate()
    )}`;
    const uid = `${Date.now()}-flowchest-check@flowchest`;
    const dtStamp = this.formatIcsUtcDate(new Date());

    return [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Flowchest//Availability check//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${dtStamp}`,
      `DTSTART;VALUE=DATE:${dayStart}`,
      `DTEND;VALUE=DATE:${dayEnd}`,
      `SUMMARY:${this.escapeIcsText(summary)}`,
      `DESCRIPTION:${this.escapeIcsText(description)}`,
      "END:VEVENT",
      "END:VCALENDAR",
      "",
    ].join("\r\n");
  }

  private formatGoogleDate(date: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, "0");
    return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
      date.getUTCDate()
    )}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}00Z`;
  }

  /**
   * Use the classic `render?action=TEMPLATE` URL which is registered in Android's
   * Google Calendar app intent filter. The newer `/r/eventedit` path is not intercepted
   * by the native app on Android, causing it to silently fail or open a broken browser tab.
   */
  buildGoogleCalendarLink(input: CalendarEventLinkInput): string {
    const text = input.title || "Scheduled";
    const details = input.details || "";
    const location = input.location || "";

    const url = new URL(
      "https://calendar.google.com/calendar/render?action=TEMPLATE"
    );
    url.searchParams.set("text", text);
    if (details) {
      url.searchParams.set("details", details);
    }
    if (location) {
      url.searchParams.set("location", location);
    }
    if (input.start && input.end) {
      const dates = `${this.formatGoogleDate(input.start)}/${this.formatGoogleDate(
        input.end
      )}`;
      url.searchParams.set("dates", dates);
    }

    return url.toString();
  }

  /**
   * When no start/end: classic OWA compose (`/owa/?path=...`) avoids deeplink defaults that
   * prefill start/time/duration. When timed: use deeplink compose with ISO datetimes.
   */
  buildOutlookCalendarLink(input: CalendarEventLinkInput): string {
    const subject = input.title || "Scheduled";
    const body = input.details || "";
    const location = input.location || "";

    if (input.start && input.end) {
      const params = new URLSearchParams({
        path: "/calendar/action/compose",
        rru: "addevent",
        subject,
        body,
        location,
        startdt: input.start.toISOString(),
        enddt: input.end.toISOString(),
      });
      return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
    }

    const url = new URL("https://outlook.office.com/owa/");
    url.searchParams.set("path", "/calendar/action/compose");
    url.searchParams.set("rru", "addevent");
    url.searchParams.set("subject", subject);
    url.searchParams.set("body", body);
    url.searchParams.set("location", location);
    return url.toString();
  }

  downloadIcsFile(input: CalendarEventLinkInput, fileNamePrefix: string): void {
    const ics =
      input.start && input.end
        ? this.buildIcsEventContent(input)
        : this.buildIcsDraftEventForReview(input);
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const safePrefix = (fileNamePrefix || "event")
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase();
    const fileName = `${safePrefix || "event"}-${Date.now()}.ics`;

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  /** Timed event (requires start and end). */
  private buildIcsEventContent(input: CalendarEventLinkInput): string {
    const start = input.start!;
    const end = input.end!;
    const uid = `${Date.now()}-flowchest@flowchest`;
    const dtStamp = this.formatIcsUtcDate(new Date());
    const dtStart = this.formatIcsUtcDate(start);
    const dtEnd = this.formatIcsUtcDate(end);
    const summary = this.escapeIcsText(input.title || "Scheduled");
    const description = this.escapeIcsText(input.details || "");
    const location = this.escapeIcsText(input.location || "");

    return [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Flowchest//Schedule Reminder//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${dtStamp}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${location}`,
      "END:VEVENT",
      "END:VCALENDAR",
      "",
    ].join("\r\n");
  }

  /**
   * When the user did not pick times: emit a normal VEVENT (not VTODO). Tasks often import
   * into Reminders or add without a review sheet. A draft event opens the usual Calendar
   * “preview / add / edit” flow; times are placeholders the user can change before saving.
   */
  private buildIcsDraftEventForReview(input: CalendarEventLinkInput): string {
    const { start, end } = this.defaultDraftEventWindowLocal();
    const uid = `${Date.now()}-flowchest@flowchest`;
    const dtStamp = this.formatIcsUtcDate(new Date());
    const dtStart = this.formatIcsLocalFloating(start);
    const dtEnd = this.formatIcsLocalFloating(end);
    const summary = this.escapeIcsText(input.title || "Scheduled");
    const note =
      "Change the date and time below before you save, if you like.\n\n";
    const description = this.escapeIcsText(note + (input.details || ""));
    const location = this.escapeIcsText(input.location || "");

    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Flowchest//Schedule Reminder//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${dtStamp}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      "STATUS:TENTATIVE",
      "SEQUENCE:0",
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
    ];
    if (location) {
      lines.push(`LOCATION:${location}`);
    }
    lines.push("END:VEVENT", "END:VCALENDAR", "");
    return lines.join("\r\n");
  }

  /** Placeholder window: next 5-minute mark, 30 minutes long (local wall time, floating). */
  private defaultDraftEventWindowLocal(): { start: Date; end: Date } {
    const start = new Date();
    const minutes = start.getMinutes();
    start.setMinutes(Math.ceil(minutes / 5) * 5, 0, 0);
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    return { start, end };
  }

  /** Local floating time (no Z): calendar apps treat as local timezone. */
  private formatIcsLocalFloating(date: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, "0");
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
      date.getDate()
    )}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(
      date.getSeconds()
    )}`;
  }

  private formatIcsUtcDate(date: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, "0");
    return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
      date.getUTCDate()
    )}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(
      date.getUTCSeconds()
    )}Z`;
  }

  private escapeIcsText(value: string): string {
    return value
      .replace(/\\/g, "\\\\")
      .replace(/;/g, "\\;")
      .replace(/,/g, "\\,")
      .replace(/\r?\n/g, "\\n");
  }
}
